import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import axios from '../../../utils/axiosConfig';
import ModalBaseSm from '../../modals/modal-base-sm';
import FormModalCreateBeacons from '../forms/formModalCreateBeacons';

const requestConfig = () => ({
    headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('tkn')}`,
    },
    withCredentials: true,
});

const ModalBeacons = ({ bitInserts, bitEdits }) => {
    const [beacons, setBeacons] = useState([]);
    const [proyectos, setProyectos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedBeacon, setSelectedBeacon] = useState(null);

    const listarBeacons = async (term = '') => {
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER}/operaciones/getbeacons`,
                { Beacons: term },
                requestConfig()
            );

            if (!data?.success) {
                Swal.fire('Error', data?.message || 'No se pudieron cargar los beacons', 'error');
                return;
            }

            setBeacons(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
            Swal.fire('Error', 'Falla de red al cargar la lista de beacons', 'error');
        }
    };

    const getProyectos = async () => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_SERVER}/operaciones/getproyectos`,
                requestConfig()
            );

            if (!data?.success) {
                Swal.fire('Error', 'No se pudieron obtener los proyectos', 'error');
                return;
            }

            setProyectos(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
            Swal.fire('Error', 'Falla de red al obtener los proyectos', 'error');
        }
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setSelectedBeacon(null);
    };

    const openCreateDialog = () => {
        if (!bitInserts) return;
        setSelectedBeacon(null);
        setDialogOpen(true);
    };

    const openEditDialog = item => {
        if (!bitEdits) return;
        setSelectedBeacon(item);
        setDialogOpen(true);
    };

    const saveBeacon = async ({ DeviceID, fkLokProyecto }) => {
        const payload = {
            DeviceID: DeviceID.trim(),
            FKLokProyecto: fkLokProyecto,
        };

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER}/operaciones/setbeacons`,
                payload,
                requestConfig()
            );

            if (!data?.success) {
                Swal.fire('Error', data?.message || 'No se pudo guardar el beacon', 'error');
                return;
            }

            Swal.fire('Éxito', selectedBeacon ? 'Beacon actualizado' : 'Beacon guardado', 'success');
            closeDialog();
            listarBeacons(debouncedTerm);
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar el beacon', 'error');
        }
    };

    const getProyectoName = id => {
        if (id == null || id === '') return '-';
        const proyecto = proyectos.find(item =>
            String(item.IDProyecto ?? item.FkLokProyecto) === String(id)
        );
        return proyecto?.Descripcion || String(id);
    };

    const filteredBeacons = useMemo(() => {
        if (!debouncedTerm) return beacons;
        const term = debouncedTerm.toLowerCase();

        return beacons.filter(item => {
            const projectId = item.FKLokProyecto ?? item.FkLokProyecto;
            return [item.DeviceID, getProyectoName(projectId)]
                .some(value => String(value || '').toLowerCase().includes(term));
        });
    }, [beacons, debouncedTerm, proyectos]);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedTerm(searchTerm.trim()), 300);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        getProyectos();
    }, []);

    useEffect(() => {
        listarBeacons(debouncedTerm);
    }, [debouncedTerm]);

    const initialData = selectedBeacon
        ? {
            DeviceID: selectedBeacon.DeviceID,
            fkLokProyecto: selectedBeacon.FKLokProyecto ?? selectedBeacon.FkLokProyecto,
            isEditing: true,
        }
        : null;

    return (
        <>
            <style>
                {`
                    @media (max-width: 575.98px) {
                        .btn-nuevo-beacon {
                            width: 100%;
                        }
                    }
                `}
            </style>

            <Row className="mb-4 align-items-center">
                <Col sm="8">
                    <input
                        type="text"
                        className="form-input w-100"
                        placeholder="Buscar por beacon / proyecto..."
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.target.value)}
                        style={{ height: '38px', boxSizing: 'border-box' }}
                    />
                </Col>
                <Col
                    sm="4"
                    className="d-flex justify-content-center justify-content-sm-end mt-2 mt-sm-0"
                >
                    <button
                        className="Button blue btn-nuevo-beacon"
                        onClick={openCreateDialog}
                        style={{ height: '38px', minWidth: '150px' }}
                    >
                        Nuevo Beacon
                    </button>
                </Col>
            </Row>

            <div className="card ContainerTableMaestros">
                <div
                    className="table-responsive text-nowrap"
                    style={{ height: '300px', overflowY: 'auto' }}
                >
                    <table className="table table-hover" style={{ width: '100%' }}>
                        <thead className="FondoHeaderTabla2">
                            <tr>
                                <th style={{ color: '#ffffff', textAlign: 'center', width: '45%' }}>
                                    Device ID
                                </th>
                                <th style={{ color: '#ffffff', textAlign: 'center', width: '45%' }}>
                                    Proyecto
                                </th>
                                <th style={{ color: '#ffffff', textAlign: 'center', width: '10%' }}>
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBeacons.map(item => (
                                <tr key={`${item.DeviceID}-${item.FKLokProyecto ?? item.FkLokProyecto ?? ''}`}>
                                    <td style={{ color: '#000' }}>{item.DeviceID || '-'}</td>
                                    <td style={{ color: '#000', textAlign: 'center' }}>
                                        {getProyectoName(item.FKLokProyecto ?? item.FkLokProyecto)}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            aria-label={`Editar beacon ${item.DeviceID || ''}`}
                                            onClick={() => openEditDialog(item)}
                                        >
                                            <i
                                                className="bx bxs-pencil bx-tada"
                                                style={{ color: '#006699' }}
                                            />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <ModalBaseSm
                isOpen={dialogOpen}
                title={selectedBeacon ? 'Editar Beacon' : 'Crear Beacon'}
                onClose={closeDialog}
                childComponent={
                    <FormModalCreateBeacons
                        onSave={saveBeacon}
                        initialData={initialData}
                        proyectos={proyectos}
                    />
                }
            />
        </>
    );
};

export default ModalBeacons;
